import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SessionService } from '@app/store/session/session.service';
import { Permission, Role } from '@app/store/session/session.store';
import { environment } from '@env/environment';
import { BehaviorSubject, Observable } from 'rxjs';

export interface IMenuItem {
  id?: string;
  title?: string;
  description?: string;
  type: string;       // Possible values: link/dropDown/extLink
  name?: string;      // Used as display text for item and title for separator type
  state?: string;     // Router state
  icon?: string;      // Material icon name
  tooltip?: string;   // Tooltip text
  disabled?: boolean; // If true, item will not be appeared in sidenav.
  sub?: IChildItem[]; // Dropdown items
  badges?: IBadge[];
  active?: boolean;
}
export interface IChildItem {
  id?: string;
  parentId?: string;
  type?: string;
  name: string;       // Display text
  state?: string;     // Router state
  icon?: string;
  sub?: IChildItem[];
  active?: boolean;
}

interface IBadge {
  color: string;      // primary/accent/warn/hex color codes(#fff000)
  value: string;      // Display text
}

interface ISidebarState {
  sidenavOpen?: boolean;
  childnavOpen?: boolean;
}

interface RequestedAuctionRefundsHelper {
  numRequested: number;
}

const REQUESTED_AUCTION_REFUNDS_COUNT_TTL = 30 * 60 * 1000; // 30 minutes

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  public sidebarState: ISidebarState = {
    sidenavOpen: true,
    childnavOpen: true
  };
  constructor(
    private http: HttpClient, private userService: SessionService
  ) {
    this.getFastQuoteCount();
  }

  defaultMenu: IMenuItem[] = [
    {
      name: 'Produto',
      description: '',
      type: 'link',
      icon: 'person',
      state: '/painel/product'
    },
    {
      name: 'Notificações',
      description: '',
      type: 'dropDown',
      icon: 'notifications',
      sub: [
        { name: 'Push App', state: '/painel/notification/app', type: 'link' },
        { name: 'E-mail', state: '/painel/notification/email', type: 'link' }
      ]
    }
  ];

  menuItems = new BehaviorSubject<IMenuItem[]>(this.defaultMenu);
  menuItems$ = this.menuItems.asObservable();

  private fastQuoteCount = new BehaviorSubject<number>(null);
  fastQuoteCount$: Observable<number> = this.fastQuoteCount.asObservable();

  private requestedAuctionRefundsCount = new BehaviorSubject<number>(0);
  requestedAuctionRefundsCount$: Observable<number> =
    this.requestedAuctionRefundsCount.asObservable();
  private requestedAuctionRefundsCountFetchedAt: number | null = null;
  private requestedAuctionRefundsCountRequestInFlight = false;
  private requestedAuctionRefundsCountRefreshPending = false;

  getFastQuoteCount() {
    this.userService.getUser().subscribe({
      next: userInfo => {
        if (!userInfo?.hasRoles(Role.ADMIN, Role.DEALER)) {
          this.fastQuoteCount.next(0);
          return;
        }

        this.http.get(`${environment.api}/fastQuote/countActive`).subscribe({
          next: (resp: any) => {
            if (!resp) {
              this.fastQuoteCount.next(0);
            } else {
              this.fastQuoteCount.next(resp.numActive)
            }
          },
          error: () => {
            this.fastQuoteCount.next(0);
          }
        });
      },
      error: () => {
        this.fastQuoteCount.next(0);
      }
    });
  }

  getRequestedAuctionRefundsCount(forceRefresh = false): void {
    this.userService.getUser().subscribe({
      next: userInfo => {
        const canManageRefunds =
          userInfo?.hasRole(Role.ADMIN) ||
          userInfo?.hasAnyPermission(
            Permission.GLOBAL_ACCESS,
            Permission.MANAGE_AUCTION_REFUNDS
          );

        if (!canManageRefunds) {
          this.requestedAuctionRefundsCount.next(0);
          this.requestedAuctionRefundsCountFetchedAt = null;
          return;
        }

        if (this.requestedAuctionRefundsCountRequestInFlight) {
          if (forceRefresh) {
            this.requestedAuctionRefundsCountRefreshPending = true;
          }
          return;
        }

        const cacheIsFresh =
          this.requestedAuctionRefundsCountFetchedAt !== null &&
          Date.now() - this.requestedAuctionRefundsCountFetchedAt <
            REQUESTED_AUCTION_REFUNDS_COUNT_TTL;
        if (!forceRefresh && cacheIsFresh) {
          return;
        }

        this.requestedAuctionRefundsCountRequestInFlight = true;
        this.http
          .get<RequestedAuctionRefundsHelper>(`${environment.api}/auctionRefund/countRequested`)
          .subscribe({
            next: response => {
              this.requestedAuctionRefundsCount.next(response?.numRequested ?? 0);
              this.requestedAuctionRefundsCountFetchedAt = Date.now();
              this.finishRequestedAuctionRefundsCountRequest();
            },
            error: () => {
              this.requestedAuctionRefundsCount.next(0);
              this.requestedAuctionRefundsCountFetchedAt = null;
              this.finishRequestedAuctionRefundsCountRequest();
            },
          });
      },
      error: () => {
        this.requestedAuctionRefundsCount.next(0);
        this.requestedAuctionRefundsCountFetchedAt = null;
      },
    });
  }

  private finishRequestedAuctionRefundsCountRequest(): void {
    this.requestedAuctionRefundsCountRequestInFlight = false;

    if (this.requestedAuctionRefundsCountRefreshPending) {
      this.requestedAuctionRefundsCountRefreshPending = false;
      this.getRequestedAuctionRefundsCount(true);
    }
  }

  incrementFastQuoteCount() {
    let currentValue = this.fastQuoteCount.getValue();
    this.fastQuoteCount.next(currentValue + 1);
  }

  decrementFastQuoteCount() {
    let currentValue = this.fastQuoteCount.getValue();
    this.fastQuoteCount.next(currentValue > 0 ? currentValue - 1 : 0);
  }
}
